import { withErrorBoundary } from 'react-error-boundary';
import styled from 'styled-components';
import type { TypeArticleCardDetailed } from 'types/autogen/contentful';
import { Typography } from 'components/generic/typography';
import { removeListStyling } from 'modules/helpers/mixins';
import { Spacing16, Spacing8 } from 'autogen/design-tokens/tokens';
import ContentCard from 'components/ContentCard';
import type { ContentCardProps } from 'components/ContentCard/ContentCard';
import { navigationLinkToPageAction } from '../../shared/ContentfulHelpers';
import DynamicItemCarousel from 'components/generic/ScrollSnapSlider/components/DynamicItemCarousel';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import { createSrcSet } from '../Image/shared/helpers';

type TypeFields = NonNullable<TypeArticleCardDetailed['fields']>;
type TypeFieldLinks = NonNullable<TypeFields['links']>;

const GridLayout = styled.ul`
    ${removeListStyling}
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: ${Spacing16};
`;

const HeaderText = styled(Typography.Headline)`
    margin-block: ${Spacing8};
`;

function LinkItem({ link, ...restProps }: { link: TypeFieldLinks[number] }) {
    if (!link) return null;

    const {
        fields: { headline, imageRatio43, section, linkText },
        sys: { id }
    } = link;

    const linkAction = navigationLinkToPageAction(link);

    if (!linkAction) return null;

    const imgSrc = imageRatio43?.fields.image?.fields.file?.url;
    const imgAlt = imageRatio43?.fields.altText;
    const imgFile = imageRatio43?.fields.image?.fields.file;

    if (!imgSrc || !imgAlt) return null;

    const inspectorProps = useContentfulInspectorMode({ entryId: id });

    const cardProps: ContentCardProps = {
        link: {
            label: linkText,
            action: linkAction
        },
        section: section?.fields.publicName,
        title: headline,
        image: {
            src: imgSrc,
            alt: imgAlt,
            srcSet: imgFile ? createSrcSet(imgFile) : '',
            sizes: '250px'
        }
    };

    return <ContentCard {...cardProps} {...restProps} {...inspectorProps({ fieldId: 'internalName' })} />;
}

function Grid({ links }: { links: TypeFieldLinks }) {
    const linkItems = links.map((link, index) => (
        <li key={link?.sys.id || index}>
            <LinkItem link={link} />
        </li>
    ));

    return <GridLayout>{linkItems}</GridLayout>;
}

function Slider({ links }: { links: TypeFieldLinks }) {
    return (
        <DynamicItemCarousel
            gapSize={Spacing16}
            items={links}
            itemRenderer={(link, index) => <LinkItem link={link} key={index} data-slide-index={index} />}
        />
    );
}

function Layout({ layout, links }: { layout: TypeFields['layout']; links: TypeFieldLinks }) {
    switch (layout) {
        case 'Slider':
            return <Slider links={links} />;
        case 'Grid':
        default:
            return <Grid links={links} />;
    }
}

function ArticleCard(props: TypeArticleCardDetailed) {
    const {
        fields: { layout, links, title },
        sys: { id }
    } = props;

    if (!links) return;

    const inspectorProps = useContentfulInspectorMode({ entryId: id });

    return (
        <div {...inspectorProps({ fieldId: 'internalName' })}>
            <HeaderText size="M">{title}</HeaderText>
            <Layout layout={layout} links={links} />
        </div>
    );
}

export default withErrorBoundary(ArticleCard, {
    fallback: null,
    onError(error, info) {
        console.error(error, info);
    }
});
